import { useMemo, useState } from "react";
import { Button, SearchBox, tokens } from "@fluentui/react-components";
import {
  ArrowRight16Regular,
  ThumbDislike16Regular,
  ThumbLike16Regular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { helpText } from "./strings";
import * as Sentry from "@sentry/react";
import { ASK_AREA_LABEL, ASK_CATALOG, type AskEntry } from "./ask/catalog";
import { buildIndex, searchCatalog } from "./ask/search";
import { flashAnchor } from "./dom";
import { PAGE_LOAD_MS, revealHelpStep, useRevealNavigation } from "./reveal";
import { bringOffer, startNewOffer } from "./ask/journey";
import { helpForPath } from "./registry";
import { EnPill } from "./HelpTour";
import { viewerHas, type HelpLanguage, type HelpTranslation, type HelpViewer } from "./types";

interface Props {
  viewer: HelpViewer;
  language: HelpLanguage;
  /** Get the drawer out of the way before the page is pressed or outlined. */
  onHandover: () => void;
  notify: (message: string) => void;
}

/**
 * "Where do I do X?" — answered from the reviewed catalog in ask/catalog.ts,
 * about any page. Nothing is sent anywhere except anonymous usage logging.
 */
export function HelpAsk({ viewer, language, onHandover, notify }: Props) {
  const t = helpText(language);
  const navigate = useNavigate();
  const navigation = useRevealNavigation(language);
  const [query, setQuery] = useState("");
  const [asked, setAsked] = useState("");
  const [feedback, setFeedback] = useState<Record<string, boolean>>({});

  const index = useMemo(
    () => buildIndex(ASK_CATALOG.filter((e) => !e.requires || viewerHas(viewer, e.requires))),
    [viewer]
  );
  const hits = useMemo(() => (asked ? searchCatalog(index, asked) : []), [index, asked]);

  const ask = () => {
    const q = query.trim();
    setAsked(q);
    setFeedback({});
    if (q) {
      const found = searchCatalog(index, q).map((h) => h.entry.id);
      Sentry.logger.info("help.ask.query", { query: q, results: found.join(",") });
    }
  };

  const takeMeThere = async (entry: AskEntry) => {
    Sentry.logger.info("help.ask.navigate", { id: entry.id });
    const needs = entry.i18n?.[language as HelpTranslation]?.needs ?? entry.needs ?? "";
    const newOffer = /^\/offer\?type=(mtpl|casco)$/.exec(entry.route)?.[1] as "mtpl" | "casco" | undefined;
    const onNewOffer = window.location.pathname === "/offer";
    const stays = newOffer && onNewOffer && new URLSearchParams(window.location.search).get("type") === newOffer;
    if (onNewOffer && !stays && !navigation.confirmLeave()) return;
    onHandover();
    if (entry.offer) {
      if (!(await bringOffer(entry.offer, navigate))) {
        return notify(t("help.ask.noOffer", "No offer on the list can do this right now. {{needs}}", { needs }));
      }
    } else if (newOffer) {
      // through the list, as a person would: a direct jump from an open offer keeps that offer's state
      if (!stays && !(await startNewOffer(newOffer, navigate))) {
        return notify(t("help.ask.notFound", "Opened the page, but the control is not on screen. {{needs}}", { needs }));
      }
    } else {
      navigate(entry.route);
    }
    const anchor = entry.anchor;
    if (!anchor) return;
    const notFound = () =>
      notify(t("help.ask.notFound", "Opened the page, but the control is not on screen. {{needs}}", { needs }));
    // the control may live in a tab / menu / dialog the guide knows how to open
    const page = helpForPath(entry.route.split("?")[0]);
    const step = page?.steps.find((s) => s.selector === `[data-help="${anchor}"]`);
    if (page && step) await revealHelpStep(page, step, undefined, PAGE_LOAD_MS);
    flashAnchor(anchor, { activate: entry.open, onMissing: notFound });
  };

  const rate = (entry: AskEntry, helped: boolean) => {
    setFeedback((f) => ({ ...f, [entry.id]: helped }));
    Sentry.logger.info("help.ask.feedback", { id: entry.id, helped, query: asked });
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <SearchBox
          data-help="help-ask-input"
          style={{ flex: 1, maxWidth: "none" }}
          placeholder={t("help.ask.placeholder", "Ask how to do something…")}
          value={query}
          onChange={(_, d) => setQuery(d.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") ask();
          }}
        />
      </div>
      <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3, margin: "6px 0 12px" }}>
        {t("help.ask.hint", 'Plain words work best — "new CASCO offer", "mark invoice paid".')}
      </div>

      <div data-help="help-ask-results">
        {!asked && (
          <div style={{ color: tokens.colorNeutralForeground3, fontSize: 13 }}>
            {t("help.ask.empty", "Ask a question about any screen in Quoting Gate.")}
          </div>
        )}
        {asked && hits.length === 0 && (
          <div style={{ color: tokens.colorNeutralForeground3, fontSize: 13 }}>
            {t(
              "help.ask.noAnswer",
              'No answer for that yet. Try other words, or open "This page" for the guide of the page you are on.'
            )}
          </div>
        )}
        {hits.map(({ entry }) => {
          const tr = language === "en" ? undefined : entry.i18n?.[language];
          const translated = language === "en" || (!!tr?.title && !!tr?.body);
          const area = ASK_AREA_LABEL[entry.area][language];
          const rated = feedback[entry.id];
          return (
            <article
              key={entry.id}
              style={{
                padding: "12px 14px",
                marginBottom: 10,
                borderRadius: 8,
                border: `1px solid ${tokens.colorNeutralStroke2}`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    padding: "2px 8px",
                    borderRadius: 10,
                    color: tokens.colorBrandForeground1,
                    background: tokens.colorBrandBackground2,
                  }}
                >
                  {area}
                </span>
                <span style={{ flex: 1 }} />
                {!translated && <EnPill language={language} />}
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{tr?.title ?? entry.title}</div>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: tokens.colorNeutralForeground2 }}>
                {tr?.body ?? entry.body}
              </p>
              {entry.needs && (
                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: 12,
                    fontStyle: "italic",
                    color: tokens.colorPaletteDarkOrangeForeground1,
                  }}
                >
                  {t("help.ask.needs", "Needs:")} {tr?.needs ?? entry.needs}
                </p>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <Button
                  size="small"
                  appearance="outline"
                  icon={<ArrowRight16Regular />}
                  iconPosition="after"
                  onClick={() => void takeMeThere(entry)}
                >
                  {entry.open
                    ? t("help.ask.goOpen", "Take me there and open it")
                    : t("help.ask.go", "Take me there")}
                </Button>
                <span style={{ fontSize: 11, color: tokens.colorNeutralForeground3 }}>{entry.id}</span>
                <span style={{ flex: 1 }} />
                <Button
                  size="small"
                  appearance={rated === true ? "primary" : "subtle"}
                  icon={<ThumbLike16Regular />}
                  aria-label={t("help.ask.helped", "Answer helped")}
                  disabled={rated !== undefined}
                  onClick={() => rate(entry, true)}
                />
                <Button
                  size="small"
                  appearance={rated === false ? "primary" : "subtle"}
                  icon={<ThumbDislike16Regular />}
                  aria-label={t("help.ask.notHelped", "Answer did not help")}
                  disabled={rated !== undefined}
                  onClick={() => rate(entry, false)}
                />
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
